import { cloneElement, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FloppyDisk, SignOut } from '@phosphor-icons/react';

import pt from '../../locales/pt';
import { authAPI } from '../../data/auth';
import { useAuth } from '../../store/AuthContext';
import { Button } from '../ui/Button';
import {
  WizardPhasedProgress,
  WizardSkeleton,
} from '../wizard';
import { OnboardingDesktopRail } from '../onboarding/OnboardingDesktopRail';
import { ONBOARDING_PHASES, ONBOARDING_STEP_LABELS } from '../../lib/onboardingCatalogs';
import { ONBOARDING_PATH_TO_STEP } from '../../lib/onboardingStepPaths';
import { cn } from '../../utils/cn';
import { ScrollToTop } from './ScrollToTop';

const TOTAL_STEPS = 9;

const STEP_TO_INDEX = {
  '/onboarding/welcome': 1,
  '/onboarding/profile': 2,
  '/onboarding/cycle-day': 3,
  '/onboarding/method': 4,
  '/onboarding/income-sources': 5,
  '/onboarding/categories': 6,
  '/onboarding/accounts': 7,
  '/onboarding/debts': 8,
  '/onboarding/ready': 9,
};

const STEP_TO_PREV = {
  '/onboarding/profile': { path: '/onboarding/welcome', step: 'welcome' },
  '/onboarding/cycle-day': { path: '/onboarding/profile', step: 'profile' },
  '/onboarding/method': { path: '/onboarding/cycle-day', step: 'cycle_day' },
  '/onboarding/income-sources': { path: '/onboarding/method', step: 'method' },
  '/onboarding/categories': { path: '/onboarding/income-sources', step: 'income_sources' },
  '/onboarding/accounts': { path: '/onboarding/categories', step: 'categories' },
  '/onboarding/debts': { path: '/onboarding/accounts', step: 'accounts' },
  '/onboarding/ready': { path: '/onboarding/debts', step: 'debts' },
};

export function OnboardingLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, refreshMe } = useAuth();
  const step = STEP_TO_INDEX[location.pathname] ?? 1;
  const prev = STEP_TO_PREV[location.pathname];
  const isReady = location.pathname === '/onboarding/ready';

  const [footer, setFooter] = useState(null);
  const [shellLoading, setShellLoading] = useState(false);
  const prevStepRef = useRef(step);
  const [direction, setDirection] = useState('forward');

  useEffect(() => {
    const prevStep = prevStepRef.current;
    if (step !== prevStep) {
      setDirection(step < prevStep ? 'back' : 'forward');
      prevStepRef.current = step;
    }
    setShellLoading(false);
  }, [location.pathname, step]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  /**
   * «Guardar e sair» (.pen topbar do onboarding + `ds/wizard/header`): persiste o
   * passo actual com a API de progresso que já existe (`saveOnboardingStep`) e
   * termina a sessão — o utilizador retoma exactamente aqui no próximo login
   * (`resolveOnboardingPath`). Não há API nova de rascunho.
   */
  const handleSaveExit = useCallback(async () => {
    const stepKey = ONBOARDING_PATH_TO_STEP[location.pathname];
    if (stepKey) await authAPI.saveOnboardingStep(stepKey);
    await logout();
    navigate('/');
  }, [location.pathname, logout, navigate]);

  const handleBack = useCallback(async () => {
    if (!prev) return;
    await authAPI.saveOnboardingStep(prev.step);
    await refreshMe();
    navigate(prev.path);
  }, [navigate, prev, refreshMe]);

  const setFooterStable = useCallback((node) => {
    setFooter(node);
  }, []);

  // Always inject the live layout back handler — step footers must not capture a stale onBack.
  const footerWithLiveBack = useMemo(() => {
    if (!footer || !isValidElement(footer)) return footer;
    return cloneElement(footer, {
      onBack: prev ? handleBack : undefined,
      backLabel: pt.onboarding.common.back,
    });
  }, [footer, handleBack, prev]);

  const logoutLabel = pt.settings.hub.logout.title;

  return (
    <div className="flex h-[100dvh] max-h-[100dvh] w-full flex-col overflow-hidden bg-background">
      <ScrollToTop />

      <div className="flex min-h-0 flex-1 flex-col lg:grid lg:grid-cols-[320px_minmax(0,1fr)]">
        <div className="hidden min-h-0 lg:block">
          {/* Logout vive no topbar (.pen 1280), não duplicado no rail. */}
          <OnboardingDesktopRail step={step} />
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* Topbar — .pen frames `Onboarding · … · 375|768|1280`: wordmark
              + «Guardar e sair» + terminar sessão (icon-only a 375, com label a partir
              de 768). A 1280 o wordmark vive no rail lateral, por isso o `.pen`
              desliga-o aqui e alinha as acções à direita. */}
          <header className="z-30 shrink-0 border-b border-border bg-card/80 backdrop-blur lg:border-b-0 lg:bg-transparent lg:backdrop-blur-none">
            <div className="flex items-center justify-between px-space-5 py-space-4 md:px-space-8 md:py-space-5 lg:justify-end">
              <Link
                to="/"
                className="type-title-card font-display tracking-tight text-foreground lg:hidden"
              >
                {pt.app.name}
              </Link>

              <div className="flex items-center gap-space-3 md:gap-space-4">
                <button
                  type="button"
                  onClick={handleSaveExit}
                  className="inline-flex shrink-0 items-center gap-space-1 whitespace-nowrap text-type-label font-semibold text-muted-foreground transition hover:text-foreground"
                >
                  <FloppyDisk className="size-3.5 shrink-0" weight="regular" aria-hidden />
                  {pt.wizard.common.saveExit}
                </button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  aria-label={logoutLabel}
                  /* .pen: icon-only a 375, com label a partir de 768 (`max-*` do
                     Tailwind não é gerado neste config — usar variantes positivas). */
                  className="size-10 shrink-0 justify-center whitespace-nowrap rounded-ds-md p-0 text-type-label text-muted-foreground md:h-auto md:w-auto md:rounded-ds-full md:px-space-3 md:py-space-2"
                >
                  <SignOut className="size-5 shrink-0 md:size-4" weight="regular" aria-hidden />
                  <span className="hidden md:inline">{logoutLabel}</span>
                </Button>
              </div>
            </div>
          </header>

        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col overflow-hidden',
            /* Tablet: sage radial canvas + centered card — .pen */
            'md:items-center md:justify-center md:bg-[radial-gradient(120%_90%_at_50%_0%,theme(colors.sage.50),theme(colors.background))] md:px-6 md:py-6',
            'dark:md:bg-[radial-gradient(120%_90%_at_50%_0%,theme(colors.sage.950),theme(colors.background))]',
            /* Desktop: flat main column */
            'lg:items-stretch lg:justify-start lg:bg-background lg:px-0 lg:py-0 dark:lg:bg-background',
          )}
        >
          <main
            className={cn(
              'container-app flex min-h-0 flex-1 flex-col overflow-hidden py-0',
              'md:h-full md:w-full md:max-w-[600px] md:px-0',
              'lg:max-w-none lg:flex-1',
            )}
          >
            <WizardSkeleton
              wizardTitle={pt.onboarding.shellTitle}
              step={step}
              total={TOTAL_STEPS}
              showBack={false}
              direction={direction}
              loading={shellLoading}
              showProgress={!isReady}
              progress={
                <div className="lg:hidden">
                  <WizardPhasedProgress
                    step={step}
                    total={TOTAL_STEPS}
                    phases={ONBOARDING_PHASES}
                  />
                </div>
              }
              footer={footerWithLiveBack}
              chromeClassName="lg:hidden"
              bodyClassName={cn('lg:px-0 lg:pt-space-8', isReady && 'pt-space-4')}
              footerClassName="bg-background/95 backdrop-blur md:bg-card/95 lg:bg-background/95 lg:px-0 lg:pb-space-6 lg:pt-space-4"
              className={cn(
                'h-full min-h-0 flex-1',
                'md:h-full md:max-h-full md:max-w-[600px] md:rounded-ds-xl md:shadow-lg',
                /* Desktop reading column — .pen shell 600 */
                'lg:mx-auto lg:w-full lg:max-w-[600px] lg:rounded-none lg:border-0 lg:bg-transparent lg:shadow-none',
              )}
            >
              <Outlet
                context={{
                  onboardingBack: prev ? handleBack : null,
                  step,
                  stepLabel: ONBOARDING_STEP_LABELS[step - 1] ?? '',
                  setFooter: setFooterStable,
                  setShellLoading,
                  direction,
                }}
              />
            </WizardSkeleton>
          </main>
        </div>
        </div>
      </div>
    </div>
  );
}
